import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, LinkButton } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import Permissions from '@/common/permissions';
import { Option } from '@/common/types';
import { useHasAccess } from '@/modules/core/hooks';
import AddNewProcessingRuleTitle from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/AddNewProcessingRuleTitle';
import {
  COMPLEX_CONFIGURATION_SETUP_FIELD,
  NEW_PROCESSING_RULE_KEY,
} from '@/modules/HealthPlan/HealthPlanSetup/HealthPlanSetup.constants';
import { useHealthPlanFieldsState, useTemplateFieldState } from '@/modules/HealthPlan/HealthPlanSetup/hooks';
import theme from '@/styles/theme';
import { useFieldsWithDefaultValues } from '@/utils';

import { useEnrollmentMatrixStore } from '../stores';

export const MANUAL_CLAIM_KEY = 'MANUAL_CLAIM';
export const HEALTH_PLAN_KEY = 'HEALTH_PLAN';

export default (
  isActivationMode?: boolean,
  isTemplateMode?: boolean,
  isEditMode?: boolean,
  processingRulesList?: Option[],
  isActiveStatus?: boolean,
  parentTemplate?: string,
  onEditProcessingRule?: () => void,
  isLoadingRules?: boolean,
  handleGetLoadingData?: () => void,
  processingRuleSourceId?: string,
  isFieldLoading?: boolean
) => {
  const currentState = useEnrollmentMatrixStore((state) => state.state);
  const errorFields = useEnrollmentMatrixStore((state) => state.errorFields);

  const hasCreateEditProcessingRuleAccess = useHasAccess([
    {
      permission: Permissions.SET_UP_HP_PROCESSING_RULE,
    },
    {
      permission: Permissions.SET_UP_PROCESSING_DEFINITION,
    },
  ]);
  const matrixNameDisabled = useMemo(() => {
    if (isTemplateMode && isEditMode && isActiveStatus) {
      return true;
    }
    return !isTemplateMode;
  }, [isTemplateMode, isEditMode, isActiveStatus]);

  const onlyActivationRequired = useMemo(
    () => (isActivationMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING),
    [isActivationMode]
  );

  const fields = useMemo<Field[]>(
    () => [
      {
        name: COMPLEX_CONFIGURATION_SETUP_FIELD.ENROLLMENT_MATRIX_NAME,
        type: FieldTypes.Text,
        label: 'Enrollment matrix name',
        placeholder: 'Enter enrollment matrix name',
        disabled: matrixNameDisabled,
        showRequireIcon: true,
        defaultValue: currentState?.enrollmentMatrixName,
        validator:
          !isActivationMode && errorFields?.enrollmentMatrixName
            ? VALIDATORS.STRING.test({
                test: (val) =>
                  !errorFields?.enrollmentMatrixName || val !== errorFields?.enrollmentMatrixName?.value,
                message: errorFields?.enrollmentMatrixName?.message,
              })
            : onlyActivationRequired,
      },
      {
        name: COMPLEX_CONFIGURATION_SETUP_FIELD.PROCESSING_RULE_NAME,
        type: FieldTypes.Dropdown,
        label: 'Processing rule',
        singleMode: true,
        disabled: !!parentTemplate || isActiveStatus,
        isLoading: !!parentTemplate || isLoadingRules,
        isFieldLoading: isFieldLoading,
        handleGetLoadingData: handleGetLoadingData,
        options: [
          ...(hasCreateEditProcessingRuleAccess
            ? [
                {
                  key: NEW_PROCESSING_RULE_KEY,
                  primary: true,
                  value: 'Create a New Processing Rule',
                  node: <AddNewProcessingRuleTitle />,
                },
                {
                  divide: true,
                  isGroupDivider: true,
                  groupKey: 'Other',
                  groupDividerTitle: '',
                },
              ]
            : []),
          ...(processingRulesList || []),
        ] as Option[],
        defaultValue: currentState?.processingRuleName,
        placeholder: 'Select processing rule',
        validator: onlyActivationRequired,
        showRequireIcon: true,
        fieldControlStyle: {
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
        childrenNode:
          hasCreateEditProcessingRuleAccess &&
          processingRuleSourceId &&
          isEditMode &&
          !isActiveStatus &&
          parentTemplate ? (
            <Box margin={{ top: theme.spacings.spacing8 }}>
              <LinkButton onClick={onEditProcessingRule}>Customize Processing Rule</LinkButton>
            </Box>
          ) : undefined,
      },
      {
        name: COMPLEX_CONFIGURATION_SETUP_FIELD.DEDUCTIBLE_SERVICE,
        type: FieldTypes.Radio,
        defaultValue: currentState?.deductibleService,
        label: 'Add service configs for claims',
        options: [
          { label: 'Manual claims', value: MANUAL_CLAIM_KEY },
          { label: 'Carrier claims', value: HEALTH_PLAN_KEY },
        ],
        showRequireIcon: true,
        validator: onlyActivationRequired,
      },
    ],
    [
      matrixNameDisabled,
      currentState?.enrollmentMatrixName,
      currentState?.processingRuleName,
      currentState?.deductibleService,
      isActivationMode,
      errorFields?.enrollmentMatrixName,
      onlyActivationRequired,
      parentTemplate,
      isLoadingRules,
      isActiveStatus,
      handleGetLoadingData,
      hasCreateEditProcessingRuleAccess,
      processingRulesList,
      processingRuleSourceId,
      isEditMode,
      onEditProcessingRule,
      isFieldLoading,
    ]
  );

  const filteredField = useHealthPlanFieldsState(fields);
  const formatedFields = useFieldsWithDefaultValues(filteredField, currentState);
  const stateFields = useTemplateFieldState(
    formatedFields,
    isTemplateMode,
    isEditMode,
    undefined,
    COMPLEX_CONFIGURATION_SETUP_FIELD.DEDUCTIBLE_SERVICE
  );
  return stateFields;
};
